import type { Numeric, NumericValue } from '@/utils/numeric';
import type { CultivarSalesProfile } from './cultivar';

// nursery.ts — TypeScript types for the Nursery Management module

export type ReadinessMode = 'lead_time' | 'seasonal_window' | 'manual';
export type LeadTimeAnchor = 'pot_date' | 'plant_date' | 'graft_date' | 'last_movement';

export interface NurseryTreeType {
  id: number;
  code: string;
  name: string;
  stock_type: 'B' | 'P';
  organisation_id: number | null;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  readiness_mode: ReadinessMode;
  lead_time_days: number | null;
  lead_time_anchor: LeadTimeAnchor;
  season_start_month: number | null;
  season_end_month: number | null;
  created_at: string;
  updated_at: string;
}

export interface TreeTypeUpdatePayload {
  name?: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
  readiness_mode?: ReadinessMode;
  lead_time_days?: number | null;
  lead_time_anchor?: LeadTimeAnchor;
  season_start_month?: number | null;
  season_end_month?: number | null;
}

export interface NurseryRow {
  id: number;
  organisation_id: number;
  block_id: number;
  row_number: number;
  row_length_meters: Numeric;
  row_spacing_meters: Numeric;
  capacity: number | null;
  notes: string | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  block_name?: string;
  farm_name?: string;
}

export interface NurseryStockItem {
  id: number;
  organisation_id: number;
  cultivar_id: number;
  rootstock_id: number | null;
  tree_type_id: number;
  sku_code: string | null;
  description: string | null;
  website_price: Numeric;
  /** The tree's own barcode: what a retailer's purchase order names it by. */
  barcode?: string | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  cultivar_name?: string;
  cultivar_trade_name?: string;
  cultivar_protection_status?: string;
  /** The cultivar's crop ("Lemon"). Null when the cultivar has none. */
  crop_type_id?: number | null;
  crop_type_name?: string | null;
  rootstock_name?: string;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
}

/**
 * An umbrella product: one customer-facing name and price over several stock
 * items, chosen by hand in the order they are preferred ("Dwarf Eureka Lemon"
 * = Eureka / Flying Dragon, Eureka / Trifoliata). Stock and picking keep
 * seeing the real stock items.
 */
export interface NurseryProductMember {
  product_id: number;
  stock_item_id: number;
  sort_order: number;
  active: boolean;
  sku_code: string | null;
  cultivar_name: string;
  cultivar_protection_status: string | null;
  /** The cultivar's crop ("Lemon"). A product is listed under its first member's. */
  crop_type_id?: number | null;
  crop_type_name?: string | null;
  rootstock_name: string | null;
  tree_type_id?: number;
  tree_type_code: string;
  tree_type_name: string;
  stock_type: 'B' | 'P';
}

export interface NurseryProduct {
  id: number;
  organisation_id: number;
  name: string;
  /** Chosen with the name; every member is this tree type. */
  tree_type_id: number | null;
  tree_type_code?: string | null;
  tree_type_name?: string | null;
  /** Sold on an order line, so its tree type is fixed. */
  has_orders?: boolean;
  sku_code: string | null;
  description: string | null;
  website_price: Numeric;
  /** The product's own barcode: what a retailer's purchase order names it by. */
  barcode?: string | null;
  tag_colour_name: string | null;
  tag_colour_hex: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  members: NurseryProductMember[];
}

export interface ProductPayload {
  name: string;
  tree_type_id: number;
  sku_code?: string | null;
  description?: string | null;
  website_price?: number | null;
  barcode?: string | null;
  tag_colour_name?: string | null;
  tag_colour_hex?: string | null;
  active?: boolean;
  /** Stock items it is filled from, most preferred first. */
  stock_item_ids: number[];
}

export interface NurseryBatch {
  id: number;
  organisation_id: number;
  batch_number: string;
  name: string;
  stock_type: 'B' | 'P';
  description: string | null;
  target_date: string | null;
  status: 'open' | 'closed';
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export type NurseryGrowingStatus =
  | 'planted' | 'growing' | 'budded' | 'graded'
  | 'allocated' | 'dug' | 'sold' | 'removed';

export interface NurseryGrowing {
  id: number;
  organisation_id: number;
  stock_item_id: number;
  batch_id: number;
  row_id: number;
  sequence_start: number;
  sequence_end: number | null;
  planted_date: string | null;
  budded_date: string | null;
  potted_date: string | null;
  ready_date: string | null;
  dig_date: string | null;
  sold_date: string | null;
  planted_count: number;
  beg_count: number | null;
  first_count: number | null;
  second_count: number | null;
  current_count: number;
  status: NurseryGrowingStatus;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  cultivar_id?: number;
  rootstock_id?: number;
  tree_type_id?: number;
  sku_code?: string;
  cultivar_name?: string;
  cultivar_protection_status?: string;
  rootstock_name?: string;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
  batch_number?: string;
  batch_name?: string;
  row_number?: number;
  block_name?: string;
  farm_name?: string;
}

export interface NurserySourceRecord {
  id: number;
  organisation_id: number;
  growing_id: number;
  source_type: 'rootstock_planting' | 'graftwood' | 'budwood' | 'cutting' | 'seedling' | 'potting' | 'division' | 'purchase';
  source_growing_id: number | null;
  source_cultivar_id: number | null;
  source_supplier: string | null;
  source_batch_ref: string | null;
  quantity: number | null;
  source_date: string | null;
  notes: string | null;
  recorded_by_user_id: number | null;
  created_at: string;
  // Joined
  source_growing_status?: string;
  source_cultivar_name?: string;
}

export interface NurseryGrading {
  id: number;
  organisation_id: number;
  growing_id: number;
  grading_pass: number;
  grading_date: string;
  count_before: number;
  first_grade_count: number;
  second_grade_count: number;
  cull_count: number;
  graded_by_user_id: number | null;
  notes: string | null;
  created_at: string;
  graded_by_name?: string;
}

export type NurseryOrderStatus =
  | 'draft' | 'confirmed' | 'allocated' | 'partially_allocated'
  | 'picking' | 'picked' | 'shipped' | 'invoiced'
  | 'completed' | 'cancelled';

/** How the nursery sends an order (docs/nursery-despatch-plan.md). */
export type NurseryDespatchMethod = 'run' | 'carrier' | 'post' | 'collection' | 'cross_dock';

export const DESPATCH_METHOD_LABELS: Record<NurseryDespatchMethod, string> = {
  run: 'Our delivery run',
  carrier: 'Carrier',
  post: 'Post',
  collection: 'Customer collects',
  cross_dock: 'Cross dock',
};

export type NurseryOrderPriority = 'normal' | 'asap';

export interface NurseryOrder {
  id: number;
  nursery_org_id: number;
  customer_org_id: number | null;
  customer_id: number | null;
  order_number: string;
  order_date: string;
  requested_delivery_date: string | null;
  estimated_delivery_date: string | null;
  status: NurseryOrderStatus;
  placed_by_org: 'nursery' | 'customer';
  total_trees: number;
  total_value: NumericValue;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  /** What the customer chose at checkout; the delivery fee is priced on it. */
  fulfilment_method?: 'pickup' | 'delivery';
  delivery_postcode?: string | null;
  /** Snapshot of the address it goes to, taken when the address was chosen. */
  delivery_address?: string | null;
  delivery_fee?: NumericValue;
  payment_status?: 'not_required' | 'pending' | 'paid' | 'failed';
  paid_at?: string | null;
  despatch_method?: NurseryDespatchMethod;
  despatch_from?: string | null;
  despatch_by?: string | null;
  priority?: NurseryOrderPriority;
  collection_at?: string | null;
  delivery_address_id?: number | null;
  /** Doubles as the order's region when loads are planned. */
  delivery_zone_id?: number | null;
  /** Set on a backorder: the order it was split from. */
  parent_order_id?: number | null;
  /**
   * Through a cross dock: the route and delivery number the schedule gave the
   * store for its pick day (docs/retailer-po-and-cross-dock-plan.md).
   * xdock_schedule_id is null when the pick day was chosen by hand.
   */
  xdock_schedule_id?: number | null;
  xdock_route?: string | null;
  xdock_del_seq?: string | null;
  /** Set on an order made from a retailer's purchase order file. */
  po_import_id?: number | null;
  // Joined
  customer_name?: string;
  nursery_name?: string;
  delivery_zone_name?: string | null;
  parent_order_number?: string | null;
  load_id?: number | null;
  load_number?: string | null;
  load_despatch_date?: string | null;
  load_status?: NurseryLoadStatus | null;
  /** Paid online for trees that were never picked, at the price charged. */
  refund_due?: NumericValue | null;
  /** Set when the order was cancelled. A reason is optional. */
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  /** The customer's purchase order number. Printed on the docket and invoice. */
  customer_po?: string | null;
  lines?: NurseryOrderLine[];
  invoices?: NurseryInvoice[];
  /** Order page only: who pays, and whether a PO is required. */
  account?: {
    payer_id: number;
    payer_name: string;
    parent_id: number | null;
    parent_name: string | null;
    po_required: boolean;
    terms: import('./nurseryAccounts').PaymentTerms | null;
  } | null;
  /** Order page only: where the payer's account stands. Warns, never blocks. */
  credit?: import('./nurseryAccounts').CreditPosition | null;
  /** Order page only: the refund for a paid-online order that was short-picked. */
  refund?: import('./nurseryAccounts').OrderRefund | null;
}

export interface NurseryOrderLine {
  id: number;
  order_id: number;
  stock_item_id: number;
  quantity_ordered: number;
  quantity_allocated: number;
  quantity_picked: number;
  quantity_shipped: number;
  unit_price: Numeric;
  discount_pct: NumericValue;
  line_total: Numeric;
  gst_amount: NumericValue;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /** Set when the line was sold under an umbrella product. */
  product_id?: number | null;
  // Joined
  cultivar_name?: string;
  cultivar_protection_status?: string;
  rootstock_name?: string;
  tree_type_code?: string;
  tree_type_name?: string;
  product_name?: string | null;
  tag_colour_name?: string | null;
  tag_colour_hex?: string | null;
  /** For a product line: what its allocations actually drew on. */
  filled_from?: { cultivar_name: string; rootstock_name: string | null; quantity: number }[] | null;
}

export interface NurseryAllocation {
  id: number;
  organisation_id: number;
  order_line_id: number;
  lot_id: number;
  quantity: number;
  allocated_date: string;
  allocated_by_user_id: number | null;
  status: 'allocated' | 'picked' | 'shipped' | 'cancelled';
  created_at: string;
}

// ─── Loads and the despatch planner (docs/nursery-despatch-plan.md) ───

export type NurseryLoadType = 'run' | 'carrier' | 'post' | 'collection' | 'ad_hoc' | 'cross_dock';

export const LOAD_TYPE_LABELS: Record<NurseryLoadType, string> = {
  run: 'Delivery run',
  carrier: 'Carrier',
  post: 'Post',
  collection: 'Collections',
  ad_hoc: 'Local ad hoc',
  cross_dock: 'Cross dock',
};

export type NurseryLoadStatus = 'planning' | 'released' | 'picking' | 'loaded' | 'despatched' | 'cancelled';

/** Where an order stands against the planning window. */
export type DueClass = 'due' | 'overdue' | 'undated';

/** An order as the planner and a load see it. */
export interface DespatchOrder {
  id: number;
  order_number: string;
  status: NurseryOrderStatus;
  customer_id: number | null;
  customer_name: string | null;
  /** The retailer's store number, the customer's purchase order, and what the cross dock schedule said. */
  customer_code?: string | null;
  customer_po?: string | null;
  xdock_route?: string | null;
  xdock_del_seq?: string | null;
  despatch_method: NurseryDespatchMethod;
  priority: NurseryOrderPriority;
  despatch_from: string | null;
  despatch_by: string | null;
  requested_delivery_date: string | null;
  collection_at: string | null;
  delivery_zone_id: number | null;
  delivery_zone_name: string | null;
  delivery_address: string | null;
  delivery_postcode: string | null;
  parent_order_id: number | null;
  total_trees: number;
  allocated_trees: number;
  /** The farms its allocated stock sits at; a null farm_id is a lot with no location. */
  stock_farms: { farm_id: number | null; farm_name: string | null; quantity: number }[];
  load_id: number | null;
  stop_sequence: number | null;
  load_number: string | null;
  load_despatch_date: string | null;
  load_status: NurseryLoadStatus | null;
  load_type: NurseryLoadType | null;
  /** The delivery address's coordinates, when it was picked from Places. */
  delivery_lat: NumericValue | null;
  delivery_lng: NumericValue | null;
  scheduled_notice_date: string | null;
  despatched_notice_at: string | null;
  // Planner only
  due_class?: DueClass;
  fully_allocated?: boolean;
}

export interface LoadCheck {
  code: 'UNALLOCATED' | 'NEEDS_TRANSFER' | 'STOCK_FARM_UNKNOWN' | 'NO_PICKER' | 'OVER_CAPACITY' | 'OUTSIDE_ZONES' | 'EMPTY';
  severity: 'warning' | 'info';
  message: string;
  order_ids?: number[];
}

export interface NurseryLoad {
  id: number;
  organisation_id: number;
  load_number: string;
  load_type: NurseryLoadType;
  is_daily: boolean;
  despatch_farm_id: number;
  despatch_farm_name: string;
  despatch_date: string;
  status: NurseryLoadStatus;
  zone_ids: number[];
  zone_names: string[];
  vehicle: string | null;
  driver_name: string | null;
  carrier_name: string | null;
  consignment_ref: string | null;
  capacity_trees: number | null;
  notes: string | null;
  released_at: string | null;
  despatched_at: string | null;
  created_at: string;
  updated_at: string;
  total_trees: number;
  // List only
  order_count?: number;
  // Detail only
  picker_count?: number;
  despatch_farm_point?: { lat: number; lng: number } | null;
  orders?: DespatchOrder[];
  checks?: LoadCheck[];
}

export interface CreateLoadPayload {
  load_type: NurseryLoadType;
  despatch_farm_id: number;
  despatch_date: string;
  zone_ids?: number[];
  vehicle?: string | null;
  driver_name?: string | null;
  carrier_name?: string | null;
  consignment_ref?: string | null;
  capacity_trees?: number | null;
  notes?: string | null;
  order_ids?: number[];
}

export type UpdateLoadPayload = Partial<Omit<CreateLoadPayload, 'load_type' | 'order_ids'>>;

// ─── Picking (docs/nursery-despatch-plan.md, Phase 2) ───

export type PickShortReason = 'not_found' | 'count_low' | 'not_ready' | 'damaged' | 'other';

export const SHORT_REASON_LABELS: Record<PickShortReason, string> = {
  not_found: 'Could not find them',
  count_low: 'Fewer there than recorded',
  not_ready: 'Not ready yet',
  damaged: 'Damaged or not saleable',
  other: 'Something else',
};

/** A load a picker can work. No prices anywhere in picking. */
export interface PickLoad {
  id: number;
  load_number: string;
  load_type: NurseryLoadType;
  is_daily: boolean;
  despatch_date: string;
  status: NurseryLoadStatus;
  despatch_farm_id: number;
  despatch_farm_name: string;
  order_count: number;
  item_count: number;
  picked_count: number;
  total_trees: number;
  first_collection_at: string | null;
}

export interface PickLoadsResponse {
  loads: PickLoad[];
  /** How many farms a picker-only user is assigned; null for everyone else. */
  farms_assigned: number | null;
}

/** One allocation to pick. */
export interface PickItem {
  allocation_id: number;
  quantity: number;
  allocation_status: string;
  substituted_from_allocation_id: number | null;
  order_line_id: number;
  order_id: number;
  order_number: string;
  collection_at: string | null;
  despatch_method: NurseryDespatchMethod;
  customer_name: string | null;
  /** Who the order is for; a load is picked one customer at a time (pickCustomers.ts). */
  customer_id?: number | null;
  customer_org_id?: number | null;
  customer_code?: string | null;
  /** The server's key for the customer: 'c<id>', 'o<org id>' or 'order<id>'. Claims use it. */
  customer_key?: string;
  stop_sequence: number | null;
  lot_id: number;
  lot_code: string | null;
  lot_count: number;
  lot_ready_date: string | null;
  lot_stage: string;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  /** The order line's notes: a sticker to apply, a comment from the purchase order. */
  line_notes?: string | null;
  /** The umbrella product the line was sold as, when it was. */
  sold_as?: string | null;
  tag_colour_name?: string | null;
  tag_colour_hex?: string | null;
  location_name: string | null;
  block_name: string | null;
  /** The block's place in the farm's walk; null when none is set. */
  block_sort_order?: number | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  farm_id: number | null;
  farm_name: string | null;
  at_despatch_farm: boolean;
  pick: {
    quantity_picked: number;
    status: 'picked' | 'short';
    short_reason: PickShortReason | null;
    notes: string | null;
    picked_at: string;
    picked_by_name: string | null;
  } | null;
}

export interface PickList {
  load: Pick<NurseryLoad, 'id' | 'load_number' | 'load_type' | 'is_daily' | 'despatch_date' | 'status'
    | 'despatch_farm_id' | 'despatch_farm_name' | 'vehicle' | 'driver_name' | 'notes'>;
  items: PickItem[];
  /** Who is picking for which customer right now. Absent from an older server. */
  claims?: PickClaim[];
  progress: { items: number; resolved: number; trees: number; picked_trees: number };
}

/** One picker's hold on a customer on a load; another picker cannot pick for them. */
export interface PickClaim {
  customer_key: string;
  user_id: number;
  user_name: string | null;
  claimed_at: string;
  last_active_at: string;
  mine: boolean;
}

export interface PickAlternative {
  lot_id: number;
  lot_code: string | null;
  current_count: number;
  ready_date: string | null;
  /** What the lot is — an umbrella product's lots are not all the same tree. */
  cultivar_name?: string;
  rootstock_name?: string | null;
  location_name: string | null;
  block_name: string | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  available: number;
}

// ─── Despatch, transfers and refills (docs/nursery-despatch-plan.md, Phase 3) ───

/** What one order on a load was short, for the despatch dialog. */
export interface LoadShortfall {
  order_id: number;
  order_number: string;
  customer_name: string | null;
  despatch_from: string | null;
  despatch_by: string | null;
  paid_online: boolean;
  lines: {
    line_id: number;
    cultivar_name: string;
    rootstock_name: string | null;
    tree_type_name: string;
    ordered: number;
    picked: number;
    short: number;
  }[];
}

export interface DespatchChoice {
  order_id: number;
  action: 'backorder' | 'cancel';
  despatch_from?: string | null;
  despatch_by?: string | null;
}

export interface DespatchResult {
  load_id: number;
  load_number: string;
  movements: number;
  backorders: { order_id: number; order_number: string; parent_order_number: string; trees: number }[];
  cancelled: string[];
  /** Refill transfers the despatch triggered. */
  refills: string[];
  /** Invoices made final by the despatch. */
  invoices_authorised?: number;
  /** Left as drafts because a line has no price: billing it at $0 is not despatch's call. */
  invoices_held?: { invoice_id: number; invoice_number: string; order_number: string; lines: string[] }[];
  /** Refunds issued for paid-online orders that were short. */
  refunds?: { order_id: number; amount: number; status: 'pending' | 'succeeded' | 'failed'; error: string | null }[];
  /** Lots whose count was lower than what was picked from them: set to 0, never a reason to stop the load. */
  lots_short?: { lot_id: number; lot_code: string | null; showed: number; picked: number; order_number: string }[];
}

export type TransferStatus = 'planned' | 'in_transit' | 'received' | 'cancelled';

export interface NurseryTransferLine {
  id: number;
  transfer_id: number;
  lot_id: number;
  quantity: number;
  allocation_id: number | null;
  received_quantity: number | null;
  to_lot_id: number | null;
  notes: string | null;
  lot_code: string | null;
  lot_count: number;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  location_name: string | null;
  block_name: string | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  order_number: string | null;
  customer_name: string | null;
  to_lot_code: string | null;
}

export interface NurseryTransfer {
  id: number;
  transfer_number: string;
  from_farm_id: number;
  from_farm_name: string;
  to_farm_id: number;
  to_farm_name: string;
  to_location_id: number;
  to_location_name: string;
  scheduled_date: string;
  status: TransferStatus;
  source: 'manual' | 'load' | 'refill';
  load_id: number | null;
  load_number: string | null;
  refill_rule_id: number | null;
  vehicle: string | null;
  driver_name: string | null;
  notes: string | null;
  sent_at: string | null;
  received_at: string | null;
  total_trees: number;
  // List only
  line_count?: number;
  received_trees?: number;
  // Detail only
  lines?: NurseryTransferLine[];
}

export interface CreateTransferPayload {
  from_farm_id: number;
  to_farm_id: number;
  to_location_id: number;
  scheduled_date: string;
  vehicle?: string | null;
  driver_name?: string | null;
  notes?: string | null;
  lines: { lot_id: number; quantity: number; allocation_id?: number | null }[];
}

export interface RefillRule {
  id: number;
  location_id: number;
  stock_item_id: number;
  target_quantity: number;
  refill_quantity: number;
  source_farm_id: number | null;
  source_farm_name: string | null;
  active: boolean;
  last_checked_at: string | null;
  last_problem: string | null;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  on_hand: number;
  refill_due: boolean;
  open_transfer: { id: number; transfer_number: string; status: TransferStatus } | null;
}

export interface DespatchArea {
  id: number;
  name: string;
  location_type: string;
  farm_id: number | null;
  farm_name: string | null;
  block_name: string | null;
  rules: RefillRule[];
}

export interface RefillRulePayload {
  location_id: number;
  stock_item_id: number;
  target_quantity: number;
  refill_quantity: number;
  source_farm_id?: number | null;
  active?: boolean;
}

// ─── Customer notices (Phase 4) ───

export type NoticeKind = 'scheduled' | 'despatched';

/**
 * The supplier manifest Bunnings' greenlife cross dock wants with a truck:
 * one row per purchase order. Trees come from the picks once picking has
 * started (`items_from`), else from the order; the packing is typed here.
 */
export interface SupplierManifestHeader {
  supplier_code?: string | null;
  batch_number?: string | null;
  phac_numbers?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  total_stacks?: number | null;
  total_pallets?: number | null;
}

export interface SupplierManifestOrder {
  order_id: number;
  order_number: string;
  invoice_number?: string | null;
  /** D and the invoice number, as the old system numbered dockets. Null until the order is invoiced. */
  docket_number?: string | null;
  customer_po: string | null;
  customer_name: string | null;
  customer_code: string | null;
  /** The store's name in Bunnings' own list, as the sheet will show it. */
  store_name?: string | null;
  xdock_route: string | null;
  xdock_del_seq: string | null;
  ordered_trees: number;
  picked_trees: number;
  items: number;
  items_from: 'picked' | 'ordered';
  trays: number | null;
  stillages: number | null;
  seedling_racks: number | null;
  /** Rides on that order's stillage: no stillage count of its own. */
  shares_with_order_id?: number | null;
}

export interface SupplierManifestEmail {
  recipients: string[];
  sent_ok: boolean;
  error: string | null;
  sent_at: string;
  sent_by: string | null;
}

export interface SupplierManifest {
  load: { id: number; load_number: string; load_type: NurseryLoadType; status: NurseryLoadStatus; despatch_date: string };
  supplier: { name: string; address: string };
  header: SupplierManifestHeader;
  /** False until someone saves this load's manifest; the header is then a starting point. */
  saved: boolean;
  /** The organisation's usual recipients. */
  recipients?: string[];
  /** The latest sends of this load's manifest, newest first. */
  emails?: SupplierManifestEmail[];
  orders: SupplierManifestOrder[];
}

export interface NoticePreview {
  kind: NoticeKind;
  load: { id: number; load_number: string; despatch_date: string; status: NurseryLoadStatus };
  nursery_name: string;
  contact_email: string | null;
  orders: {
    order_id: number;
    order_number: string;
    customer_name: string | null;
    email: string | null;
    already_told: boolean;
    told_for_date: string | null;
    told_at: string | null;
    subject: string;
    text: string;
  }[];
}

export interface NoticeResult {
  kind: NoticeKind;
  results: { order_id: number; order_number: string; outcome: 'sent' | 'no_email' | 'already_told' | 'failed'; error?: string; email?: string }[];
}

export interface DespatchPlanner {
  from: string;
  to: string;
  orders: DespatchOrder[];
  summary: {
    orders: number;
    trees: number;
    on_load: number;
    not_on_load: number;
    not_on_load_trees: number;
    overdue_not_on_load: number;
    undated: number;
    on_load_unallocated: number;
    asap_not_on_load: number;
  };
  farms: { id: number; name: string; picker_count: number }[];
  /** Loads not yet despatched or cancelled: the ones orders can be added to. */
  open_loads: NurseryLoad[];
}

export interface NurseryInvoice {
  id: number;
  nursery_org_id: number;
  customer_org_id: number | null;
  customer_id: number | null;
  order_id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  subtotal: NumericValue;
  gst_total: NumericValue;
  total_amount: NumericValue;
  /** 'sent' means authorised; 'cancelled' means void. Overdue is derived, never stored. */
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  authorised_at?: string | null;
  amount_paid?: NumericValue;
  amount_credited?: NumericValue;
  xero_sync_status?: 'pending' | 'pushing' | 'synced' | 'failed' | null;
  // Joined
  customer_name?: string;
  order_number?: string;
}

/** A ready lot matching an order line's SKU, net of existing allocations. */
export interface AvailableStock {
  id: number;
  lot_code: string;
  current_count: number;
  current_stage: string;
  ready_date: string | null;
  location_name: string | null;
  location_type: string | null;
  available_count: number;
  cultivar_name?: string;
  rootstock_name?: string | null;
}

// ─── Payload types ───

export interface CreateRowPayload {
  block_id: number;
  row_number: number;
  row_length_meters?: number;
  row_spacing_meters?: number;
  capacity?: number;
  notes?: string;
}

export interface BulkCreateRowsPayload {
  block_id: number;
  start_row: number;
  end_row: number;
  row_length_meters?: number;
  row_spacing_meters?: number;
  capacity?: number;
}

export interface CreateStockItemPayload {
  cultivar_id: number;
  rootstock_id?: number;
  tree_type_id: number;
  sku_code?: string;
  description?: string;
  website_price?: number | null;
  barcode?: string | null;
}

export interface WebsiteSignup {
  id: number;
  user_id: number;
  organisation_id: number;
  email: string;
  customer_number: string | null;
  status: 'pending' | 'linked' | 'dismissed';
  linked_customer_id: number | null;
  linked_by_user_id: number | null;
  linked_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  first_name?: string | null;
  last_name?: string | null;
  user_email?: string;
  organisation_name?: string | null;
  linked_customer_name?: string | null;
}

export interface LinkWebsiteSignupPayload {
  customer_id?: number;
  customer_name?: string;
  customer_tier_id?: number | null;
}

export interface CreateBatchPayload {
  batch_number: string;
  name: string;
  stock_type: 'B' | 'P';
  description?: string;
  target_date?: string;
}

export interface CreateGrowingPayload {
  stock_item_id: number;
  batch_id: number;
  row_id: number;
  sequence_start?: number;
  sequence_end?: number;
  planted_date?: string;
  potted_date?: string;
  planted_count: number;
  notes?: string;
}

export interface CreateSourceRecordPayload {
  source_type: NurserySourceRecord['source_type'];
  source_growing_id?: number;
  source_cultivar_id?: number;
  source_supplier?: string;
  source_batch_ref?: string;
  quantity?: number;
  source_date?: string;
  notes?: string;
}

export interface RecordGradingPayload {
  grading_pass: number;
  grading_date: string;
  count_before: number;
  first_grade_count?: number;
  second_grade_count?: number;
  cull_count?: number;
  notes?: string;
}

export interface CreateOrderPayload {
  customer_id?: number;
  customer_org_id?: number;
  order_date: string;
  requested_delivery_date?: string;
  estimated_delivery_date?: string;
  placed_by_org?: 'nursery' | 'customer';
  notes?: string;
  customer_po?: string | null;
  lines?: OrderLinePayload[];
  despatch_method?: NurseryDespatchMethod;
  despatch_from?: string | null;
  despatch_by?: string | null;
  priority?: NurseryOrderPriority;
  collection_at?: string | null;
  delivery_address_id?: number | null;
  delivery_zone_id?: number | null;
}

/** The despatch fields an order edit may carry. */
/**
 * One row of the order sheet: a stock item or an umbrella product, the ready
 * stock behind it, and what the order being entered already holds of it.
 */
export interface OrderSheetRow {
  /** "s:<stock item id>" or "p:<product id>". */
  key: string;
  stock_item_id: number | null;
  product_id: number | null;
  name: string;
  rootstock_name: string | null;
  sku_code: string | null;
  tag_colour_name: string | null;
  tag_colour_hex: string | null;
  crop_type_name: string | null;
  tree_type_id: number;
  tree_type_code: string;
  tree_type_name: string;
  tree_type_sort: number;
  stock_type: 'B' | 'P';
  /** Ready trees, at the chosen farm or all of them. */
  ready: number;
  /** Ready trees not allocated to an order. */
  free: number;
  /** Trees other confirmed orders want and have not been allocated. */
  wanted: number;
  quantity: number;
  unit_price: number | null;
  discount_pct: number;
  line_total: number;
  /** More than 1 means the order has several lines for this row; change it on the order page. */
  line_count: number;
  /** Its cultivar or rootstock has a description or talking points to read. Absent from an older server. */
  has_info?: boolean;
}

/** One cultivar of something the nursery sells, as staff read about it. */
export interface NurseryCultivarInfo {
  cultivar_id: number;
  name: string;
  trade_name: string | null;
  crop_type_name: string | null;
  protection_status: string | null;
  role: 'variety' | 'rootstock';
  description: string | null;
  sales_profile: CultivarSalesProfile | null;
}

/** What to read about: a stock item, or an umbrella product and all its members. */
export type CultivarInfoSubject = { stock_item_id: number } | { product_id: number };

export interface OrderSheetOrder {
  id: number;
  order_number: string;
  status: NurseryOrderStatus;
  total_trees: number;
  total_value: number;
  /** Quantities can be changed on the sheet: a draft or confirmed order. */
  editable: boolean;
}

export interface OrderSheet {
  farms: { id: number; name: string }[];
  rows: OrderSheetRow[];
  order: OrderSheetOrder | null;
}

/** What saving one cell returns: the line as it now stands, and the order's totals. */
export interface OrderSheetLineResult {
  line: { id: number; quantity: number; unit_price: number | null; discount_pct: number; line_total: number } | null;
  order: { id: number; total_trees: number; total_value: number };
}

export type OrderDespatchPayload = Pick<CreateOrderPayload,
  'despatch_method' | 'despatch_from' | 'despatch_by' | 'priority' |
  'collection_at' | 'delivery_address_id' | 'delivery_zone_id'>;

/** A holder of the nursery Order Picker seat and the farms they pick at. */
export interface NurseryPicker {
  user_id: number;
  name: string;
  email: string | null;
  farm_ids: number[];
}

export interface NurseryPickersResponse {
  pickers: NurseryPicker[];
  farms: { id: number; name: string }[];
}

/** A line names a stock item or an umbrella product — exactly one. */
export interface OrderLinePayload {
  stock_item_id?: number;
  product_id?: number;
  quantity_ordered: number;
  unit_price?: number;
  discount_pct?: number;
  notes?: string;
}

export interface AllocatePayload {
  order_line_id: number;
  lot_id: number;
  quantity: number;
  allocated_date?: string;
}

export interface CreateInvoicePayload {
  order_id: number;
  invoice_date: string;
  due_date?: string;
  notes?: string;
}

export interface CreateTreeTypePayload {
  code: string;
  name: string;
  stock_type: 'B' | 'P';
  description?: string;
  sort_order?: number;
}

// ─── Customer tiers ───

export interface NurseryCustomerTier {
  id: number;
  organisation_id: number;
  name: string;
  code: string | null;
  default_discount_pct: NumericValue;
  sort_order: number;
  is_website_default: boolean;
  requires_approval: boolean;
  payment_mode: 'online' | 'on_account';
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomerTierPayload {
  name: string;
  code?: string;
  default_discount_pct?: number;
  sort_order?: number;
  is_website_default?: boolean;
  requires_approval?: boolean;
  payment_mode?: 'online' | 'on_account';
}

// ─── Trade-account requests (wholesale applications) ───

export interface TradeAccountRequest {
  id: number;
  organisation_id: number;
  customer_org_id: number;
  user_id: number | null;
  requested_tier_id: number | null;
  business_name: string;
  abn: string | null;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  delivery_address: string | null;
  notes: string | null;
  status: 'pending' | 'approved' | 'declined';
  review_notes: string | null;
  granted_tier_id: number | null;
  linked_customer_id: number | null;
  reviewed_by_user_id: number | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  requested_tier_name?: string | null;
  granted_tier_name?: string | null;
  customer_org_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  user_email?: string | null;
}

export interface ReviewTradeAccountPayload {
  granted_tier_id?: number | null;
  review_notes?: string;
}

// ─── Delivery zones ───

export interface NurseryDeliveryZone {
  id: number;
  organisation_id: number;
  customer_tier_id: number | null;
  name: string;
  match_type: 'all' | 'postcode_prefix' | 'postcode_list';
  match_value: string | null;
  flat_fee: NumericValue;
  free_over_subtotal: Numeric;
  active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Joined
  tier_name?: string | null;
}

export interface CreateDeliveryZonePayload {
  customer_tier_id?: number | null;
  name: string;
  match_type?: 'all' | 'postcode_prefix' | 'postcode_list';
  match_value?: string | null;
  flat_fee?: number;
  free_over_subtotal?: number | null;
  sort_order?: number;
  active?: boolean;
}

// ─── Customers (hybrid) ───

export type NurseryCustomerAddressType = 'billing' | 'delivery';

export interface NurseryCustomerAddress {
  id?: number;
  address_type: NurseryCustomerAddressType;
  label?: string | null;
  line1?: string | null;
  line2?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  /** Google Maps plus code, full (4RJ7+2V...) or short with a locality. */
  plus_code?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  instructions?: string | null;
  is_default?: boolean;
  /** From Places when the address was picked; used to order delivery stops. */
  lat?: number | null;
  lng?: number | null;
}

export interface NurseryCustomer {
  id: number;
  organisation_id: number;
  linked_org_id: number | null;
  customer_tier_id: number | null;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  abn: string | null;
  billing_address: string | null;
  delivery_address: string | null;
  billing_same_as_delivery?: boolean;
  addresses?: NurseryCustomerAddress[];
  notes: string | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  /** The account this customer is a site of (its head office). Two levels only. */
  parent_customer_id?: number | null;
  /** A site's invoices are paid by its head office unless this is false. */
  bills_to_parent?: boolean;
  /** The retailer's own number for the store or account. */
  customer_code?: string | null;
  /** NULL on each of these means: inherit from the head office, else the default. */
  payment_terms_type?: import('./nurseryAccounts').PaymentTermsType | null;
  payment_terms_days?: number | null;
  po_required?: boolean | null;
  credit_limit?: NumericValue | null;
  invoice_delivery?: import('./nurseryAccounts').InvoiceDelivery | null;
  /** The farm its orders are despatched from. NULL = the head office's. */
  despatch_farm_id?: number | null;
  despatch_farm_name?: string | null;
  // Joined
  tier_name?: string | null;
  tier_discount_pct?: number | null;
  linked_org_name?: string | null;
  parent_name?: string | null;
  parent_code?: string | null;
  parent_tier_name?: string | null;
  site_count?: number;
  /** Detail only: what it trades on once the head office's settings are counted. */
  account?: import('./nurseryAccounts').CustomerAccount | null;
}

export interface CreateCustomerPayload {
  name: string;
  linked_org_id?: number | null;
  customer_tier_id?: number | null;
  contact_name?: string;
  email?: string;
  phone?: string;
  abn?: string;
  billing_address?: string;
  delivery_address?: string;
  billing_same_as_delivery?: boolean;
  /** The whole address set; replaces what is stored. */
  addresses?: NurseryCustomerAddress[];
  notes?: string;
  parent_customer_id?: number | null;
  bills_to_parent?: boolean;
  customer_code?: string | null;
  payment_terms_type?: import('./nurseryAccounts').PaymentTermsType | null;
  payment_terms_days?: number | null;
  po_required?: boolean | null;
  credit_limit?: number | null;
  invoice_delivery?: import('./nurseryAccounts').InvoiceDelivery | null;
  despatch_farm_id?: number | null;
}

// ─── Price lists (versioned + tiered) ───

export type NurseryPriceListStatus = 'draft' | 'active' | 'archived';

/** A price-list row prices a stock item or an umbrella product. */
export interface NurseryPriceListItem {
  id: number;
  price_list_id: number;
  stock_item_id: number | null;
  product_id?: number | null;
  product_name?: string | null;
  min_quantity: number;
  unit_price: NumericValue;
  created_at: string;
  updated_at: string;
  // Joined
  cultivar_name?: string;
  rootstock_name?: string | null;
  tree_type_code?: string;
  sku_code?: string | null;
}

export interface NurseryPriceList {
  id: number;
  organisation_id: number;
  name: string;
  customer_tier_id: number | null;
  currency: string;
  effective_from: string;
  effective_to: string | null;
  status: NurseryPriceListStatus;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  /** A contract list for one customer and its sites, rather than a tier. */
  customer_id?: number | null;
  // Joined
  tier_name?: string | null;
  customer_name?: string | null;
  item_count?: number;
  items?: NurseryPriceListItem[];
}

export interface PriceListItemPayload {
  stock_item_id?: number;
  product_id?: number;
  min_quantity?: number;
  unit_price: number;
}

export interface CreatePriceListPayload {
  name: string;
  customer_tier_id?: number | null;
  customer_id?: number | null;
  currency?: string;
  effective_from: string;
  effective_to?: string | null;
  status?: NurseryPriceListStatus;
  notes?: string;
  items?: PriceListItemPayload[];
}

// ─── Licensed cultivars (stock-item picker source) ───

export interface LicensedCultivar {
  id: number;
  cultivar_name: string;
  trade_name: string | null;
  protection_status: string | null;
  /** Set on the cultivar page. A 'dual' cultivar belongs in both pickers. */
  cultivar_role?: 'scion' | 'rootstock' | 'dual' | null;
  /**
   * How this org came to be allowed to propagate it: a propagation licence
   * naming the cultivar, a signed master sublicence with the cultivar ticked
   * for this nursery, or no licence needed at all.
   */
  source: 'direct_licence' | 'master_sublicence' | 'open';
  /** Null for an open cultivar, which is propagable without any agreement. */
  license_id: number | null;
  license_number: string | null;
  is_open: boolean;
}

// ─── Location / Lot / Movement (production core) ───

export type RowSide = 'left' | 'right';

export type NurseryLocationType =
  | 'seed_bed' | 'nursery_row' | 'polyhouse' | 'container_area' | 'field' | 'other';

export interface NurseryLocation {
  id: number;
  organisation_id: number;
  name: string;
  location_type: NurseryLocationType;
  farm_id: number | null;
  block_id: number | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  boundary_geometry: unknown | null;
  center_point: unknown | null;
  area_hectares: Numeric;
  capacity: number | null;
  row_spacing_meters: Numeric;
  plant_spacing_meters: Numeric;
  notes: string | null;
  active: boolean;
  /** Pickers draw from it; refill rules keep it stocked (Phase 3). */
  is_despatch_area?: boolean;
  created_at: string;
  updated_at: string;
  // joined
  block_name?: string;
  farm_name?: string;
}

export interface CreateLocationPayload {
  name: string;
  location_type: NurseryLocationType;
  farm_id?: number | null;
  block_id?: number | null;
  row_number?: number | null;
  row_side?: RowSide | null;
  sequence_number?: number | null;
  capacity?: number | null;
  row_spacing_meters?: number | null;
  plant_spacing_meters?: number | null;
  notes?: string | null;
}

export interface CreateRowSlotsPayload {
  block_id: number;
  /** Every row from start_row to end_row. */
  start_row: number;
  end_row: number;
  /** Either side of a track through the block; empty for a block with none. */
  sides?: RowSide[];
  sequences: number[];
  capacity?: number | null;
  row_spacing_meters?: number | null;
  plant_spacing_meters?: number | null;
}

export type OriginCorner = 'bottom_left' | 'bottom_right' | 'top_left' | 'top_right';

export interface NurseryBlockLayout {
  id: number | null;
  organisation_id?: number;
  block_id: number;
  block_name?: string;
  farm_id?: number;
  farm_name?: string;
  heading_degrees: Numeric;
  origin_corner: OriginCorner;
  default_row_spacing_meters: Numeric;
  default_plant_spacing_meters: Numeric;
  notes: string | null;
  boundary_geometry?: unknown | null;
  center_point?: unknown | null;
}

export interface BlockLayoutPayload {
  heading_degrees?: number | null;
  origin_corner?: OriginCorner;
  default_row_spacing_meters?: number | null;
  default_plant_spacing_meters?: number | null;
  notes?: string | null;
}

export type LotStage =
  | 'sown' | 'seedling' | 'transplanted' | 'grafted' | 'budded'
  | 'growing' | 'potted' | 'ready' | 'dispatched';

export type LotStatus = 'active' | 'depleted' | 'sold' | 'culled' | 'archived';

export interface NurseryLot {
  id: number;
  organisation_id: number;
  lot_code: string | null;
  cultivar_id: number;
  rootstock_id: number | null;
  tree_type_id: number;
  location_id: number | null;
  current_stage: LotStage;
  status: LotStatus;
  plant_date: string | null;
  graft_date: string | null;
  bud_date: string | null;
  pot_date: string | null;
  ready_date: string | null;
  ready_date_source: 'auto' | 'manual';
  dispatched_date: string | null;
  initial_count: number;
  current_count: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // joined
  cultivar_name?: string;
  crop_type_id?: number | null;
  crop_type_name?: string | null;
  rootstock_name?: string | null;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
  readiness_mode?: ReadinessMode;
  location_name?: string | null;
  location_type?: NurseryLocationType | null;
  row_number?: number | null;
  row_side?: RowSide | null;
  sequence_number?: number | null;
  block_id?: number | null;
  block_name?: string | null;
  farm_id?: number | null;
  farm_name?: string | null;
}

export interface CreateLotPayload {
  lot_code?: string | null;
  cultivar_id: number;
  rootstock_id?: number | null;
  tree_type_id: number;
  location_id?: number | null;
  current_stage?: LotStage;
  plant_date?: string | null;
  graft_date?: string | null;
  bud_date?: string | null;
  pot_date?: string | null;
  ready_date?: string | null;
  ready_date_source?: 'auto' | 'manual';
  initial_count?: number;
  notes?: string | null;
  source_supplier?: string | null;
  source_ref?: string | null;
  source_lot_id?: number | null;
  source_cultivar_id?: number | null;
  record_origin?: boolean;
}

export type MovementEventType =
  | 'sow' | 'transplant' | 'pot_up' | 'graft' | 'bud' | 'split'
  | 'grade' | 'dig' | 'ship' | 'sell' | 'cull' | 'adjust'
  /** Picking takes trees off a lot; a pick undone or lowered puts them back. */
  | 'pick' | 'unpick' | 'transfer';

export interface NurseryMovement {
  id: number;
  organisation_id: number;
  event_type: MovementEventType;
  from_lot_id: number | null;
  to_lot_id: number | null;
  quantity: number;
  movement_date: string;
  source_supplier: string | null;
  source_ref: string | null;
  source_lot_id: number | null;
  source_cultivar_id: number | null;
  to_location_id: number | null;
  new_ready_date: string | null;
  notes: string | null;
  recorded_by_user_id: number | null;
  created_at: string;
  recorded_by_name?: string;
}

export interface MovementGradingDetail {
  grading_pass?: number;
  count_before?: number;
  first_grade_count?: number;
  second_grade_count?: number;
  cull_count?: number;
  notes?: string;
}

export interface ApplyMovementPayload {
  event_type: MovementEventType;
  from_lot_id?: number | null;
  to_lot_id?: number | null;
  quantity: number;
  movement_date: string;
  cultivar_id?: number;
  rootstock_id?: number | null;
  tree_type_id?: number;
  lot_code?: string | null;
  to_location_id?: number | null;
  source_supplier?: string | null;
  source_ref?: string | null;
  source_lot_id?: number | null;
  source_cultivar_id?: number | null;
  new_ready_date?: string | null;
  notes?: string | null;
  grading?: MovementGradingDetail;
}

export interface LotLineageNode {
  id: number;
  lot_code: string | null;
  current_stage: LotStage;
  status: LotStatus;
  current_count: number;
  ready_date: string | null;
  cultivar_name: string;
  tree_type_code: string;
  location_name: string | null;
}

export interface NurseryLotLineage {
  lots: LotLineageNode[];
  movements: NurseryMovement[];
}

export type ProductionRunType = 'dig' | 'ship' | 'grade' | 'pot' | 'custom';
export type ProductionRunStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled';

export interface NurseryProductionRunLot {
  run_id: number;
  lot_id: number;
  quantity: number | null;
  done: boolean;
  lot_code?: string | null;
  current_stage?: LotStage;
  current_count?: number;
  ready_date?: string | null;
  cultivar_name?: string;
  tree_type_code?: string;
  location_name?: string | null;
}

export interface NurseryProductionRun {
  id: number;
  organisation_id: number;
  run_type: ProductionRunType;
  name: string;
  scheduled_date: string | null;
  status: ProductionRunStatus;
  filter_snapshot: unknown | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  lot_count?: number;
  total_quantity?: number;
  lots?: NurseryProductionRunLot[];
}

export interface CreateProductionRunPayload {
  run_type: ProductionRunType;
  name: string;
  scheduled_date?: string | null;
  filter_snapshot?: unknown | null;
  notes?: string | null;
  lots?: Array<number | { lot_id: number; quantity?: number | null }>;
}

export interface ProductionBoardFilters {
  stock_type?: 'B' | 'P';
  stage?: LotStage;
  cultivar_id?: number;
  crop_type_id?: number;
  ready_from?: string;
  ready_to?: string;
  location_type?: NurseryLocationType;
  farm_id?: number;
  block_id?: number;
  row_number?: number;
  row_side?: RowSide | 'none';
  program_id?: number;
}

/** Where lots stand: a farm, a block in it, a row in that, one side of a track. */
export interface PlaceFilter {
  farm_id?: number;
  block_id?: number;
  row_number?: number;
  /** 'none' is the row with no side, in a block where other rows have one. */
  row_side?: RowSide | 'none';
}

export interface NurseryInventoryRow {
  id: number;
  organisation_id: number;
  cultivar_id: number;
  rootstock_id: number | null;
  tree_type_id: number;
  amt_growing: number;
  amt_ready: number;
  amt_on_order: number;
  amt_allocated: number;
  amt_picked: number;
  amt_shipped: number;
  est_avail_count: number;
  updated_at: string;
  cultivar_name?: string;
  rootstock_name?: string | null;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
  cultivar_protection_status?: string;
}

export interface NurseryDashboardStatsV2 {
  active_lots: number;
  total_trees: number;
  ready_now: number;
  ready_30d: number;
  ready_90d: number;
  by_stage: Array<{ current_stage: LotStage; lots: number; trees: number }>;
}

/**
 * What cancelling an order would do, and what stops it
 * (GET /nursery/orders/:id/cancellation). The cancel runs the same check.
 */
export interface OrderCancellation {
  order_id: number;
  order_number: string;
  status: NurseryOrderStatus;
  can_cancel: boolean;
  /** Sentences, each saying what stops it and what to do instead. */
  blockers: string[];
  /** Per lot: trees going back to free stock, and how many of those were already picked. */
  lots: { lot_id: number; lot_code: string | null; allocated: number; picked: number }[];
  trees_released: number;
  trees_picked: number;
  load: { id: number; load_number: string; status: NurseryLoadStatus } | null;
  /** Paid online: refunded to the card, in full. */
  refund_amount: number | null;
  /** Still at Stripe checkout; a payment that lands later is refunded. */
  payment_pending: boolean;
  /** Its own backorders, which are not cancelled with it. */
  backorders: { id: number; order_number: string; status: NurseryOrderStatus }[];
}

/**
 * Update prices on an order (GET /nursery/orders/:id/reprice): each line
 * against today's price book. `fill` has no price and the book now has one;
 * `change` differs from the book, and moves only when asked; `no_price` the
 * book still does not price.
 */
export interface OrderRepriceLine {
  line_id: number;
  description: string;
  quantity: number;
  discount_pct: number;
  unit_price: number | null;
  proposed_price: number | null;
  source_label: string | null;
  price_list_name: string | null;
  change: 'fill' | 'change' | 'same' | 'no_price';
}

export interface OrderReprice {
  can_reprice: boolean;
  blocker: string | null;
  invoice: { id: number; invoice_number: string; status: string } | null;
  lines: OrderRepriceLine[];
  to_fill: number;
  to_change: number;
  still_unpriced: number;
}

export interface OrderRepriceResult {
  updated: number;
  still_unpriced: number;
  /** The draft invoice written again from the new prices. */
  invoice_rebuilt: string | null;
}

export interface OrderCancelResult extends OrderCancellation {
  refund: { id: number; status: 'pending' | 'succeeded' | 'failed'; amount: NumericValue; error?: string | null } | null;
}
